import * as React from 'react';
import styled from 'styled-components';

import { NoEntitiesExist, PaginatedList, Spinner } from 'components/common';
import { GLCheckbox } from 'common/components';
import { Button } from 'components/bootstrap';
import { DataTable, Modal } from 'security-app/components/common';
import { Row } from 'security-app/components/common/FormBaseStyles';
import { usePagination, useSetPagination, useSelectedRows, useSelectedRowsDispatch } from 'common/contexts';
import { useGetEventProcedureSteps } from 'security-app/hooks/useEventProceduresAPI';
import type { EventProcedureStepAPIType } from 'security-app/hooks/api/eventProceduresAPI.types';

type Props = {
  steps: EventProcedureStepAPIType[];
  onClose: () => void;
  onAdd: (steps: EventProcedureStepAPIType[]) => void;
};

const NoEntitiesExistWrapper = styled.div`
  width: 100%;

  & > div {
    margin-top: 0;
    width: 100%;
  }
`;

function EventProcedureStepLibrary({ steps, onClose, onAdd }: Props) {
  const localPagination = usePagination();
  const setLocalPagination = useSetPagination();
  const selectedSteps = useSelectedRows();
  const selectedStepsDispatch = useSelectedRowsDispatch();

  React.useEffect(() => {
    selectedStepsDispatch({ type: 'add', payload: steps });
  }, [selectedStepsDispatch, steps]);

  const { eventProcedureSteps, loadingEventProcedureSteps, pagination } = useGetEventProcedureSteps(localPagination);

  const onPageChange = (newPage: number, newPageSize: number) => {
    setLocalPagination({ ...localPagination, page: newPage, perPage: newPageSize });
  };

  const isSelected = (eventProcedureStep: EventProcedureStepAPIType) =>
    !!selectedSteps.find((sSteps: EventProcedureStepAPIType) => eventProcedureStep.id === sSteps.id);

  const onChange = (eventProcedureStep: EventProcedureStepAPIType) => (e: React.BaseSyntheticEvent) => {
    if (e.target.checked) {
      selectedStepsDispatch({ type: 'add', payload: [eventProcedureStep] });
    } else {
      selectedStepsDispatch({ type: 'remove', payload: [eventProcedureStep] });
    }
  };

  return (
    <Modal show onClose={onClose} maxWidth="700px" title="Add Steps from Library" showClose={false}>
      {loadingEventProcedureSteps ? (
        <Spinner text="Loading event procedure steps" />
      ) : (
        <PaginatedList
          activePage={localPagination.page}
          pageSize={localPagination.perPage}
          onChange={onPageChange}
          totalItems={pagination.total}
          useQueryParameter={false}>
          <DataTable condensed striped>
            {eventProcedureSteps.length < 1 ? (
              <NoEntitiesExistWrapper>
                <NoEntitiesExist>There are no existing event procedure steps.</NoEntitiesExist>
              </NoEntitiesExistWrapper>
            ) : (
              <tbody>
                {eventProcedureSteps.map((eventProcedureStep) => (
                  <DataTable.Row key={eventProcedureStep.id}>
                    <DataTable.Item $width="70px">
                      <GLCheckbox checked={isSelected(eventProcedureStep)} onChange={onChange(eventProcedureStep)} />
                    </DataTable.Item>
                    <DataTable.Item>{eventProcedureStep.title}</DataTable.Item>
                  </DataTable.Row>
                ))}
              </tbody>
            )}
          </DataTable>
        </PaginatedList>
      )}
      <Row $justify="flex-end" $width="100%">
        <Button type="reset" onClick={onClose}>
          Cancel
        </Button>
        <Button
          type="button"
          bsStyle="success"
          disabled={selectedSteps.length < 1}
          onClick={() => onAdd(selectedSteps)}>
          Add Steps
        </Button>
      </Row>
    </Modal>
  );
}

export default EventProcedureStepLibrary;
